<!-- 丢单分析的添加 / 编辑表单弹窗 -->
<template>
  <Dialog v-model="dialogVisible" :title="dialogTitle" width="60%">
    <el-form
      ref="formRef"
      v-loading="formLoading"
      :model="formData"
      :rules="formRules"
      label-width="130px"
    >
      <el-row>
        <el-col :span="12">
          <el-form-item label="客户" prop="customerId">
            <el-select
              v-model="formData.customerId"
              class="w-1/1"
              filterable
              placeholder="请选择客户"
            >
              <el-option
                v-for="item in customerList"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="联系人" prop="contactName">
            <el-input v-model="formData.contactName" placeholder="请输入联系人" />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="24">
          <el-form-item label="丢单原因概述" prop="reasonId">
            <el-select
              v-model="formData.reasonId"
              class="w-1/1"
              filterable
              placeholder="请选择丢单原因概述"
            >
              <el-option
                v-for="item in reasonList"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="24">
          <el-form-item label="丢单原因详细说明" prop="reasonDetail">
            <el-input
              v-model="formData.reasonDetail"
              :rows="3"
              placeholder="请输入丢单原因详细说明"
              type="textarea"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="12">
          <el-form-item label="项目是否启动" prop="projectStarted">
            <el-radio-group v-model="formData.projectStarted">
              <el-radio :value="true">是</el-radio>
              <el-radio :value="false">否</el-radio>
            </el-radio-group>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="成交价格" prop="dealPrice">
            <el-input v-model="formData.dealPrice" placeholder="请输入成交价格，如 10万" />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="12">
          <el-form-item label="选择厂商" prop="vendor">
            <el-input v-model="formData.vendor" placeholder="请输入选择过的厂商" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="时间" prop="submitTime">
            <el-date-picker
              v-model="formData.submitTime"
              class="w-1/1"
              placeholder="请选择时间"
              type="datetime"
              value-format="x"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="24">
          <el-form-item label="相关文件" prop="fileUrls">
            <!-- 丢单分析附件是业务凭据，一律直接上传，不走素材库 -->
            <UploadFile
              v-model="formData.fileUrls"
              class="min-w-80px"
              :use-material-library="false"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="24">
          <el-form-item label="解决方案" prop="solution">
            <el-input
              v-model="formData.solution"
              :rows="3"
              placeholder="请输入解决方案"
              type="textarea"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="12">
          <el-form-item label="提交人">
            <el-input v-model="applicantName" disabled placeholder="提交人" />
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>
    <template #footer>
      <el-button :disabled="formLoading" type="primary" @click="submitForm">提 交</el-button>
      <el-button @click="dialogVisible = false">结 束</el-button>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { LostAnalysisApi } from '@/api/crm/lostAnalysis'
import { LostReasonApi } from '@/api/crm/lostReason'
import * as CustomerApi from '@/api/crm/customer'
import { useUserStore } from '@/store/modules/user'
import { notPureNumber } from '@/utils/formRules'

defineOptions({ name: 'LostAnalysisForm' })

const { t } = useI18n() // 国际化
const message = useMessage() // 消息弹窗
const userStore = useUserStore()

const dialogVisible = ref(false) // 弹窗的是否展示
const dialogTitle = ref('') // 弹窗的标题
const formLoading = ref(false) // 表单的加载中
const formType = ref('') // 表单的类型：create - 新增；update - 修改
const customerList = ref<any[]>([]) // 客户下拉
const reasonList = ref<any[]>([]) // 丢单原因概述下拉
const applicantName = ref('') // 提交人名称（只读）

const formData = ref<any>({
  id: undefined,
  customerId: undefined,
  contactName: undefined,
  reasonId: undefined,
  reasonDetail: undefined,
  projectStarted: undefined,
  dealPrice: undefined,
  vendor: undefined,
  fileUrls: [],
  solution: undefined,
  submitTime: undefined
})
const formRules = reactive({
  customerId: [{ required: true, message: '客户不能为空', trigger: 'change' }],
  contactName: [
    { required: true, message: '联系人不能为空', trigger: 'blur' },
    notPureNumber('联系人')
  ],
  reasonId: [{ required: true, message: '丢单原因概述不能为空', trigger: 'change' }],
  reasonDetail: [{ required: true, message: '丢单原因详细说明不能为空', trigger: 'blur' }],
  projectStarted: [{ required: true, message: '请选择项目是否启动', trigger: 'change' }]
})
const formRef = ref() // 表单 Ref

/** 打开弹窗 */
const open = async (type: string, id?: number) => {
  dialogVisible.value = true
  dialogTitle.value = type === 'create' ? '新增丢单分析' : '编辑丢单分析'
  formType.value = type
  resetForm()
  // 加载下拉数据
  customerList.value = await CustomerApi.getCustomerSimpleList()
  reasonList.value = await LostReasonApi.getLostReasonListAll()
  // 修改时，加载数据
  if (id) {
    formLoading.value = true
    try {
      const data = await LostAnalysisApi.getLostAnalysis(id)
      formData.value = {
        id: data.id,
        customerId: data.customerId,
        contactName: data.contactName,
        reasonId: data.reasonId,
        reasonDetail: data.reasonDetail,
        projectStarted: data.projectStarted,
        dealPrice: data.dealPrice,
        vendor: data.vendor,
        fileUrls: data.fileUrls || [],
        solution: data.solution,
        submitTime: data.submitTime ? new Date(data.submitTime).getTime() : undefined
      }
      applicantName.value = data.creatorName || ''
    } finally {
      formLoading.value = false
    }
  } else {
    // 新增时：时间默认当下，提交人默认当前登录用户
    formData.value.submitTime = Date.now()
    applicantName.value = userStore.getUser?.nickname || ''
  }
}
defineExpose({ open }) // 提供 open 方法，用于打开弹窗

/** 提交表单 */
const emit = defineEmits(['success']) // 定义 success 事件，用于操作成功后的回调
const submitForm = async () => {
  // 校验表单
  await formRef.value.validate()
  // 提交请求
  formLoading.value = true
  try {
    const data = { ...formData.value }
    if (formType.value === 'create') {
      await LostAnalysisApi.createLostAnalysis(data)
      message.success(t('common.createSuccess'))
    } else {
      await LostAnalysisApi.updateLostAnalysis(data)
      message.success(t('common.updateSuccess'))
    }
    dialogVisible.value = false
    // 发送操作成功的事件
    emit('success')
  } finally {
    formLoading.value = false
  }
}

/** 重置表单 */
const resetForm = () => {
  formData.value = {
    id: undefined,
    customerId: undefined,
    contactName: undefined,
    reasonId: undefined,
    reasonDetail: undefined,
    projectStarted: undefined,
    dealPrice: undefined,
    vendor: undefined,
    fileUrls: [],
    solution: undefined,
    submitTime: undefined
  }
  applicantName.value = ''
  formRef.value?.resetFields()
}
</script>
